import { useState, useEffect, useRef, useCallback, useMemo, lazy, Suspense } from 'react';
import { SessionList } from './components/SessionList';
import { AgentDetail } from './components/AgentDetail';
import { QueueChat } from './components/QueueChat';
import { LoginScreen } from './components/LoginScreen';
import { PublicPage, matchPublicRoute } from './components/PublicPage';
import { NewSessionDialog } from './components/NewSessionDialog';
import { AgentSwitchDialog } from './components/AgentSwitchDialog';
import { UpdatePrompt } from './components/UpdatePrompt';
import { MobileAgentSwitcher } from './components/MobileAgentSwitcher';
import { VoiceProvider, GlobalVoiceLayer } from './contexts/VoiceContext';

// Secondary views are code-split — they only load when the user opens
// them. Keeps the initial bundle to the core chat path (SessionList +
// AgentDetail + auth). Dashboard alone pulls the whole charts stack.
const Dashboard = lazy(() => import('./components/Dashboard').then((m) => ({ default: m.Dashboard })));
const Settings = lazy(() => import('./components/Settings').then((m) => ({ default: m.Settings })));
const RbacAdmin = lazy(() => import('./components/RbacAdmin').then((m) => ({ default: m.RbacAdmin })));
const TenantAdmin = lazy(() => import('./components/TenantAdmin').then((m) => ({ default: m.TenantAdmin })));
const BotHealth = lazy(() => import('./components/BotHealth').then((m) => ({ default: m.BotHealth })));
const HistorySearch = lazy(() => import('./components/HistorySearch').then((m) => ({ default: m.HistorySearch })));
const AdminUsers = lazy(() => import('./components/AdminUsers').then((m) => ({ default: m.AdminUsers })));
const MultiAgent = lazy(() => import('./components/MultiAgent').then((m) => ({ default: m.MultiAgent })));
const FragmentTree = lazy(() => import('./components/FragmentTree').then((m) => ({ default: m.FragmentTree })));
const AgentRiver = lazy(() => import('./components/AgentRiver').then((m) => ({ default: m.AgentRiver })));
const WorkBoard = lazy(() => import('./components/WorkBoard').then((m) => ({ default: m.WorkBoard })));
const GoalsBoard = lazy(() => import('./components/GoalsBoard').then((m) => ({ default: m.GoalsBoard })));
const CollabView = lazy(() => import('./components/CollabView').then((m) => ({ default: m.CollabView })));
const CollabListView = lazy(() => import('./components/CollabListView').then((m) => ({ default: m.CollabListView })));
const DebugConsole = lazy(() => import('./components/DebugConsole').then((m) => ({ default: m.DebugConsole })));
import { useAuth, ADMIN_EMAILS } from './hooks/useAuth';
import { useVisiblePolling } from './hooks/useVisiblePolling';
import { pullPrefs } from './lib/prefs';
import { serviceOrigin } from './lib/services';
import { setSelfIdentity } from './lib/people';
import { useSessions } from './hooks/useSessions';
import { BackgroundVideo } from './components/BackgroundVideo';
import { useEventBusConnection } from './hooks/useEventBus';
import { useDoneNotifications } from './hooks/useDoneNotifications';
import { useSessionV2 } from './hooks/useSessionV2';
import { usePools } from './hooks/usePools';
import { useTapGesture } from './hooks/useMultiTap';
import { useLaneStatus } from './hooks/useLaneStatus';
import { serviceAvailable } from './lib/services';
import { api } from './api/client';
import { StatusToasts } from './components/StatusToast';
import { GlobalToasts } from './components/GlobalToasts';

type View = 'main' | 'dashboard' | 'settings' | 'rbac' | 'tenants' | 'users' | 'multi' | 'health' | 'history' | 'fragments' | 'activity' | 'collab' | 'collabList' | 'workboard' | 'goals';

function useIsMobile() {
  const [mobile, setMobile] = useState(
    () => window.innerWidth <= 768 || window.innerHeight <= 500
  );
  useEffect(() => {
    const check = () => setMobile(window.innerWidth <= 768 || window.innerHeight <= 500);
    window.addEventListener('resize', check);
    window.addEventListener('orientationchange', check);
    return () => {
      window.removeEventListener('resize', check);
      window.removeEventListener('orientationchange', check);
    };
  }, []);
  return mobile;
}

// Trusted iframe origin allowed to request our access token via postMessage.
// content.arkturian.com hosts the /embed/post/{id} read-only view. When the
// embed boots it posts {type:'embed-ready'} to window.parent — we reply with
// {type:'embed-auth', token} so the iframe can fetch drafts the logged-in
// user is allowed to read. Targeted origin (no '*') keeps the token from
// leaking to any other site that might load us in an iframe.
const embedTrustedOrigin = (): string | null => serviceOrigin('content_web');

export default function App() {
  const { user, loading, error: authError, logout, startGoogleLogin, loginWithEmail } = useAuth();

  // Pull server-side preferences once the user is authenticated. main.tsx
  // pulls at startup too, but ONLY catches returning users whose token is
  // already in localStorage — a FRESH login (e.g. first visit to cloud2.pdrei)
  // happens AFTER that, so without this the accent/theme/filters never sync
  // from the server on a new device (Alex 2026-06-23: "andere Farbe auf pdrei").
  const prefsPulledRef = useRef(false);
  useEffect(() => {
    if (user && !prefsPulledRef.current) {
      prefsPulledRef.current = true;
      void pullPrefs();
    } else if (!user) {
      prefsPulledRef.current = false;
    }
  }, [user]);

  useEffect(() => {
    const handler = (e: MessageEvent) => {
      const trusted = embedTrustedOrigin();
      if (!trusted || e.origin !== trusted) return;
      if (e.data?.type !== 'embed-ready') return;
      if (!e.source) return;
      try {
        const raw = localStorage.getItem('ark-auth');
        if (!raw) return;
        const stored = JSON.parse(raw);
        const token = stored?.access_token;
        if (!token) return;
        (e.source as Window).postMessage(
          { type: 'embed-auth', token },
          trusted
        );
      } catch {
        /* silent — embed will fall back to anonymous fetch */
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, []);
  const [activeSession, setActiveSession] = useState<string | null>(() =>
    localStorage.getItem('cloud-active-session')
  );
  const [showSidebar, setShowSidebar] = useState(() => {
    // On mobile, if there's an active session, show conversation directly
    const hasSaved = !!localStorage.getItem('cloud-active-session');
    const mobile = window.innerWidth <= 768 || window.innerHeight <= 500;
    return !(mobile && hasSaved);
  });
  const [showNewDialog, setShowNewDialog] = useState(false);
  const [editSession, setEditSession] = useState<import('./api/types').SessionInfo | null>(null);
  const [switchSession, setSwitchSession] = useState<import('./api/types').SessionInfo | null>(null);
  const [view, setView] = useState<View>(() =>
    // A #/collab/<id> deep-link in the URL wins over the persisted view so a
    // reload lands back in the thread, not the compose screen (Alex 2026-06-23).
    /^#\/collab\/\d+/.test(window.location.hash)
      ? 'collab'
      : (localStorage.getItem('cloud-view') as View) || 'main'
  );
  const isMobile = useIsMobile();
  // Single SSE connection per tab — drives `useSessions` / `usePools` /
  // `useSessionV2`'s realtime updates. Polling continues as fallback
  // (15 s instead of 5 s) so transient SSE drops don't freeze the UI.
  useEventBusConnection();
  const { sessions, agentStates, error } = useSessions();
  // Agent names currently visible in the list (RBAC + search filter) — the
  // ambient voice-narrator roams only within this set (#1215).
  const [roamScope, setRoamScope] = useState<string[]>([]);
  // Purely SSE-driven (no poll — Alex 2026-06-22). Remote-node agents only
  // update live once Cloud federates `pane_dirty` across nodes into the shared
  // SSE bus; until then they fall back to the tab-return catch-up + F5.
  const { data } = useSessionV2(activeSession);
  // The active agent's live "busy": the conversation stream outpaces the agent-state
  // poll, so derive working from an open tool/thinking section (mirrors AgentDetail).
  // Lets the list dot match the detail header for the open agent (Alex 2026-06-29).
  // MUST stay above the view early-returns (hooks order — React #310).
  const activeBusy = useMemo(() => {
    const turns = data?.turns;
    if (!turns?.length) return false;
    const sections = turns[turns.length - 1]?.sections || [];
    for (let i = sections.length - 1; i >= 0; i--) {
      const k = sections[i].kind;
      if (k === 'text' || k === 'json') return false;
      if (k === 'tool' || k === 'thinking') return true;
    }
    return false;
  }, [data]);
  const { pools } = usePools();
  // Federation lane-status (#1251) — feeds the list badges + the blocked-toast.
  const { agents: laneAgents, byName: laneByName } = useLaneStatus();

  // Open-issue counts per agent (#180) — blue badge on each list row. One grouped query,
  // polled; silent on error (issue-api may be unreachable on some node).
  const [openIssues, setOpenIssues] = useState<Map<string, number>>(new Map());
  // Ebenfalls sichtbarkeitsgebunden (siehe useLaneStatus): Offene-Punkte-Zahlen
  // ändern sich in Minuten, nicht in Sekunden — ein verdeckter Tab braucht sie
  // gar nicht. Beim Zurückkommen wird sofort neu geladen, die Abzeichen sind
  // also nie sichtbar veraltet.
  const loadOpenIssues = useCallback(() => {
    // Instanz ohne issue-api (null im Dienst-Vertrag 77eb0c9): gar nicht erst
    // pollen — zur Aufrufzeit geprüft, weil die Instanz-Map asynchron lädt.
    if (!serviceAvailable('issue')) return;
    void api.issues.openCounts().then(setOpenIssues).catch(() => {});
  }, []);
  // 20 s waren gemessen zu teuer: Cloud zählte in 24 Stunden 8206 Abrufe der
  // Issue-Liste gegen 33 echte Vorgänge — sechsmal pro Minute der ganze
  // Bestand, für eine Zahl neben einem Namen. Ein Abruf ist hier nicht billig:
  // `openCounts` blättert je Status durch ALLE offenen Vorgänge, weil es die
  // Zählung im Browser macht.
  //
  // Der eigene Kommentar drüber sagte es schon — „ändern sich in Minuten, nicht
  // in Sekunden" — und daneben stand ein 20-Sekunden-Takt. Zwei Minuten ist,
  // was der Satz meint, und passt zu dem, was diese Zahl wert ist.
  useVisiblePolling(loadOpenIssues, 120000);

  // Mobile tap gestures (Alex 2026-06-20): double-tap → agent quickview,
  // triple-tap → group chats. Wired after openCollabList is defined.
  const [showSwitcher, setShowSwitcher] = useState(false);

  // Agents the user has actually opened, most-recent-first. The switcher
  // leads with these (then the user's OWN agents) — an admin sees ALL
  // agents globally, so sorting by raw activity surfaced strangers'
  // busy bots ("SoLieber von Martin", Alex 2026-06-20). What you touched +
  // what you own is what's relevant.
  const [recentAgents, setRecentAgents] = useState<string[]>(() => {
    try { return JSON.parse(localStorage.getItem('cloud-recent-agents') || '[]'); }
    catch { return []; }
  });

  // Group chat (CollabView). `collabPostId` null = COMPOSE mode (the view
  // itself shows the recipient line + composer; the first message creates
  // the post). Non-null = an existing thread. No overlay dialog (Alex
  // 2026-06-20: the "+" lands you straight in the chat view).
  const [collabPostId, setCollabPostId] = useState<number | null>(() => {
    // Restore the thread id from a #/collab/<id> deep-link on load.
    const m = window.location.hash.match(/^#\/collab\/(\d+)/);
    return m ? parseInt(m[1], 10) : null;
  });
  const [collabPreselect, setCollabPreselect] = useState<string[]>([]);

  // Notify (browser notification + tab-title badge) when the agent the
  // user is chatting with finishes — but only while the tab is hidden,
  // so it's silent while you're looking. Opt-in via the InputBar menu.
  const watchedForNotify = useMemo(
    () => (activeSession ? [activeSession] : []),
    [activeSession],
  );
  useDoneNotifications(agentStates, watchedForNotify);

  const [prevView, setPrevView] = useState<View | null>(null);

  // All hooks must be before any conditional returns
  const setViewPersist = useCallback((v: View) => {
    setView(v);
    localStorage.setItem('cloud-view', v);
  }, []);

  const handleSelect = useCallback((name: string) => {
    setActiveSession(name);
    localStorage.setItem('cloud-active-session', name);
    setPrevView(view);
    setViewPersist('main');
    if (isMobile) setShowSidebar(false);
    // Record as recently-used (most-recent-first, deduped, cap 20).
    setRecentAgents((prev) => {
      const next = [name, ...prev.filter((n) => n !== name)].slice(0, 20);
      localStorage.setItem('cloud-recent-agents', JSON.stringify(next));
      return next;
    });
  }, [isMobile, setViewPersist, view]);

  // Open an agent from anywhere — the IACP tool row makes its target clickable,
  // so "an Cloud" in a federation call leads straight to Cloud's conversation
  // (Alex 2026-08-02). Window event instead of prop-drilling: the tool row
  // renders inside AgentDetail AND the MultiAgent grid.
  useEffect(() => {
    const onOpenAgent = (e: Event) => {
      const name = (e as CustomEvent<{ name?: string }>).detail?.name;
      if (name) handleSelect(name);
    };
    window.addEventListener('cloud-open-agent', onOpenAgent);
    return () => window.removeEventListener('cloud-open-agent', onOpenAgent);
  }, [handleSelect]);

  const openCollabList = useCallback(() => setViewPersist('collabList'), [setViewPersist]);

  // Double-tap → agent quickview · triple-tap → group chats (mobile only).
  useTapGesture(
    { double: () => setShowSwitcher(true), triple: () => openCollabList() },
    { enabled: isMobile },
  );
  const openCollab = useCallback((postId: number) => {
    setCollabPostId(postId);
    setCollabPreselect([]);
    setViewPersist('collab');
  }, [setViewPersist]);

  // Internal deep-links to a group chat (Alex 2026-06-23). Two paths, both
  // resolve to openCollab(<id>) so a "#1207"-style reference jumps straight
  // to that chat from anywhere:
  //   1. A capture-phase click interceptor for `<a href="#/collab/<id>">`
  //      rendered in agent messages (markdown) — preventDefault + open in-app
  //      (beats target="_blank"), no markdown-renderer changes needed.
  //   2. A hash handler for external/bookmarked URLs (…/#/collab/1207).
  useEffect(() => {
    const COLLAB_HREF = /#\/collab\/(\d+)/;
    const onClick = (e: MouseEvent) => {
      const a = (e.target as HTMLElement)?.closest?.('a[href*="#/collab/"]');
      const m = a?.getAttribute('href')?.match(COLLAB_HREF);
      if (m) {
        e.preventDefault();
        openCollab(parseInt(m[1], 10));
      }
    };
    // Open the thread whenever the hash points to one (bookmark, back/forward,
    // external link). NOT cleared here — the sync effect below keeps the URL
    // tracking the view, so a reload restores the thread again.
    const onHash = () => {
      const m = window.location.hash.match(/^#\/collab\/(\d+)/);
      if (m) openCollab(parseInt(m[1], 10));
    };
    document.addEventListener('click', onClick, true);
    window.addEventListener('hashchange', onHash);
    return () => {
      document.removeEventListener('click', onClick, true);
      window.removeEventListener('hashchange', onHash);
    };
  }, [openCollab]);

  // Keep the URL hash in sync with the open thread so F5 / bookmark / share
  // round-trips back into it (Alex 2026-06-23 bug: reload dropped you onto the
  // empty compose screen because the post id lived only in React state).
  useEffect(() => {
    const want = (view === 'collab' && collabPostId != null) ? `#/collab/${collabPostId}` : null;
    const cur = window.location.hash;
    if (want && cur !== want) {
      history.replaceState(null, '', want);
    } else if (!want && cur.startsWith('#/collab/')) {
      history.replaceState(null, '', window.location.pathname + window.location.search);
    }
  }, [view, collabPostId]);
  // Compose a new group chat (postId null) — optionally with agents
  // pre-selected (e.g. the agent the "+" was pressed in).
  const openNewCollab = useCallback((preselect: string[]) => {
    setCollabPostId(null);
    setCollabPreselect(preselect);
    setViewPersist('collab');
  }, [setViewPersist]);

  const handleBack = useCallback(() => {
    if (prevView && prevView !== 'main') {
      setViewPersist(prevView);
      setPrevView(null);
    } else {
      setShowSidebar(true);
    }
  }, [prevView, setViewPersist]);

  const handleNew = () => {
    setEditSession(null);
    setShowNewDialog(true);
  };

  const handleEditAgent = (session: import('./api/types').SessionInfo) => {
    setEditSession(session);
    setShowNewDialog(true);
  };

  const handleSwitchAgent = (session: import('./api/types').SessionInfo) => {
    setSwitchSession(session);
  };

  const handleCreated = useCallback((name: string) => {
    setShowNewDialog(false);
    setEditSession(null);
    handleSelect(name);
  }, [handleSelect]);

  // Embed routes (no auth required)
  const path = window.location.pathname;
  const queueMatch = path.match(/^\/queue\/(.+)$/);
  if (queueMatch) {
    return <QueueChat sessionName={decodeURIComponent(queueMatch[1])} />;
  }

  // Public pages — no auth, and deliberately ALSO in front of the loading guard:
  // a legal page must not wait on an auth round-trip it does not need. They render
  // for logged-in users too, because /privacy and /impressum have to be reachable
  // by anyone, including an App Store reviewer without an account. Until this
  // existed, every path fell through to the login mask, so /privacy answered
  // HTTP 200 showing a sign-in form — which reads as a working link to the wrong
  // content (Alex 2026-08-01, agentos.arkturian.com; contract in post #4426).
  const publicRoute = matchPublicRoute(path);
  if (publicRoute) {
    return <PublicPage route={publicRoute} />;
  }

  // Auth guard
  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', color: 'var(--text-muted)', fontSize: 13 }}>
        Loading...
      </div>
    );
  }

  if (!user) {
    return (
      <LoginScreen
        onGoogleLogin={startGoogleLogin}
        onEmailLogin={loginWithEmail}
        error={authError}
      />
    );
  }

  // Filter sessions by ownership (non-admins only see their own).
  // Allowed_agents whitelist comes from auth-api /me — explicit grants
  // (e.g. Tommy Saier may see BackflipOneal even though he is not the owner).
  const isAdmin = !!user.is_superadmin || user.agentos_role === 'admin' || ADMIN_EMAILS.includes(user.email);
  // Canonical display identity ("Alexander Popovic") — used as the collab
  // initiator + the "my group chats" filter, so it matches how agents name
  // the human in notify_authors.
  const userName = [user.first_name, user.last_name].filter(Boolean).join(' ').trim()
    || user.email.split('@')[0];
  // Multi-email canonical identity — canonical + every alias counts as "me".
  const myEmailSet = new Set<string>(
    [user.email, ...(user.aliases || [])]
      .filter(Boolean)
      .map((e) => e.toLowerCase())
  );
  // Der Autor-Anhang „— von <Vorname>" ist am 18.08. ENTFALLEN (Alex). Ersatz
  // ist die serverseitige Attribution: `input_dedup` hält den authentifizierten
  // Absender, `/history` liefert ihn als `submitted_by`, das Portal zeigt ihn
  // am Turn. Belegt statt geraten — und ohne die Nachricht zu verändern, die
  // der Agent liest.

  // Die eigene Identität an die Namensauflösung geben: Damit steht über einer
  // eigenen Nachricht „Alexander Popovic" statt der Anmeldeadresse — ohne
  // Netzabfrage, denn diese Zuordnung kennt das Portal ohnehin (19.08.).
  setSelfIdentity([user.email, ...(user.aliases || [])], userName);

  const ownerVisible = isAdmin
    ? sessions
    : sessions.filter((s) => {
        const ownerEmail = (s.owner_email || s.owner || '').toLowerCase();
        if (myEmailSet.has(ownerEmail)) return true;
        // Explicit whitelist from user.allowed_agents
        if (Array.isArray(user.allowed_agents) && user.allowed_agents.includes(s.name)) return true;
        // Fallback: match session name against email prefix
        const prefix = user.email.split('@')[0].toLowerCase();
        if (s.name.toLowerCase().includes(prefix)) return true;
        return false;
      });
  // Drop virtual persona-slots (AiApi render-caches: api-ai-*-default/-test, superadmin)
  // from the agent list AND the collab pickers — they're not real agents and cluttered
  // the UI (Alex's "default"-ghosts; Cloud surfaced the `virtual` flag 2026-06-30). The
  // name-pattern guard is belt-and-suspenders: the collab picker still showed "superadmin"
  // + api-ai-* ghosts for admins (who see ALL sessions) where the backend `virtual` flag
  // wasn't set (Alex 2026-07-02: "Gruppenchat-Picker lauter superadmin/dev").
  const isGhostAgent = (s: { virtual?: boolean; name: string }) =>
    s.virtual === true || /^api-ai-/i.test(s.name) || s.name.toLowerCase() === 'superadmin';
  const visibleSessions = ownerVisible.filter((s) => !isGhostAgent(s));

  // The mobile quick-switcher is a fixed full-screen overlay that must sit
  // ABOVE whatever view is mounted — including the early-returned secondary
  // views (collab, dashboard, …). It used to live only in the main return's
  // JSX, so a double-tap inside a group chat set showSwitcher=true but the
  // overlay was never in the tree (Alex 2026-06-20: "im Gruppenchat sehe ich
  // die Agenten nicht"). Hoisting it into lazyView fixes that for every view.
  const switcherOverlay = showSwitcher ? (
    <MobileAgentSwitcher
      sessions={visibleSessions}
      agentStates={agentStates}
      activeSession={activeSession}
      recents={recentAgents}
      userEmail={user.email}
      userAliases={user.aliases}
      userAllowedAgents={user.allowed_agents || undefined}
      onSelect={handleSelect}
      onClose={() => setShowSwitcher(false)}
    />
  ) : null;

  // Secondary views — all lazy-loaded; the Suspense fallback shows a
  // minimal centered spinner while the chunk downloads (one-time per
  // view per session, then it's cached by the browser).
  const lazyView = (node: React.ReactNode) => (
    <Suspense fallback={<div className="view-loading">Lade…</div>}>
      {node}
      {switcherOverlay}
    </Suspense>
  );

  const showList = !isMobile || showSidebar;
  const showDetail = !isMobile || !showSidebar;

  // Der Sprachanbieter stand bis hierher UNTERHALB dieser Weiche: erst nach den
  // frühen `return`s der Nebenansichten, also nur im Hauptaufbau. Jeder Wechsel
  // auf Dashboard, Einstellungen, Verlauf oder eine Collab-Ansicht hat ihn damit
  // ausgehängt — und mit ihm das laufende Gespräch, mitten im Satz. Zwei Dinge
  // starben dabei, nicht eins: der Anbieter (und damit die Verbindung) und der
  // Tonausgang selbst, denn `<audio>` hängt in GlobalVoiceLayer
  // (VoiceContext.tsx:279). Selbst eine überlebende Verbindung hätte danach kein
  // Ziel mehr gehabt, auf dem sie klingen kann.
  //
  // Deshalb wählt die Weiche jetzt nur noch den Inhalt aus; die Hülle steht
  // darüber und bleibt über jeden Ansichtswechsel hinweg stehen. Gefunden hat
  // das CloudV2-Codex im Audit #966.
  const body = ((): React.ReactNode => {
    if (view === 'dashboard') {
      return lazyView(
        <Dashboard
          sessions={visibleSessions}
          agentStates={agentStates}
          userEmail={user.email}
          onBack={() => setViewPersist('main')}
          onSelectAgent={(name) => handleSelect(name)}
        />
      );
    }

    if (view === 'settings') {
      return lazyView(
        <Settings
          pools={pools}
          onBack={() => setViewPersist('main')}
        />
      );
    }

    if (view === 'rbac') {
      return lazyView(
        <RbacAdmin
          sessions={visibleSessions}
          onBack={() => setViewPersist('main')}
          onSelectSession={handleSelect}
        />
      );
    }

    if (view === 'tenants') {
      return lazyView(<TenantAdmin onBack={() => setViewPersist('main')} />);
    }

    if (view === 'health') {
      return lazyView(
        <BotHealth
          onBack={() => setViewPersist('main')}
          onSelectAgent={handleSelect}
          isAdmin={isAdmin}
        />
      );
    }

    if (view === 'history') {
      return lazyView(
        <HistorySearch
          sessions={visibleSessions}
          onBack={() => setViewPersist('main')}
          onSelectAgent={handleSelect}
        />
      );
    }

    if (view === 'users') {
      return lazyView(<AdminUsers onBack={() => setViewPersist('main')} />);
    }

    if (view === 'multi') {
      return lazyView(
        <MultiAgent
          sessions={visibleSessions}
          agentStates={agentStates}
          onBack={() => setViewPersist('main')}
          onSelectAgent={handleSelect}
        />
      );
    }

    if (view === 'fragments') {
      return lazyView(
        <FragmentTree
          sessions={visibleSessions}
          onBack={() => setViewPersist('main')}
        />
      );
    }

    if (view === 'activity') {
      return lazyView(
        <AgentRiver
          sessions={sessions}
          agentStates={agentStates}
          userEmail={user.email}
          userAliases={user.aliases}
          onBack={() => setViewPersist('main')}
          onSelectAgent={handleSelect}
        />
      );
    }

    if (view === 'workboard') {
      return lazyView(
        <WorkBoard
          onBack={() => setViewPersist('main')}
          onOpenAgent={handleSelect}
          agentStates={agentStates}
        />
      );
    }

    if (view === 'goals') {
      return lazyView(
        <GoalsBoard
          onBack={() => setViewPersist('main')}
          onOpenAgent={handleSelect}
        />
      );
    }

    if (view === 'collabList') {
      return lazyView(
        <CollabListView
          userName={userName}
          onOpen={openCollab}
          onNew={() => openNewCollab([])}
          onBack={() => setViewPersist('main')}
        />
      );
    }

    if (view === 'collab') {
      return lazyView(
        <CollabView
          postId={collabPostId}
          preselect={collabPreselect}
          sessions={visibleSessions}
          agentStates={agentStates}
          initiator={userName}
          onBack={openCollabList}
          onOpenAgent={handleSelect}
        />
      );
    }
    return (
      <>
        <BackgroundVideo />
        {/* Work-Board blocked/waiting toasts are FEDERATION-operator signals over
            ALL agents — for a tenant customer they confuse ("BLOCKIERT") AND leak
            other agents' states. Operator-only (Cloud IACP ddabc4cb). */}
        {isAdmin && <StatusToasts agents={laneAgents} onOpen={handleSelect} userId={user.id} />}
        {showList && (
          <SessionList
            sessions={visibleSessions}
            agentStates={agentStates}
            pools={pools}
            activeSession={activeSession}
            userEmail={user.email}
            userAliases={user.aliases}
            userAllowedAgents={user.allowed_agents || undefined}
            laneStatus={laneByName}
            openIssues={openIssues}
            isOperator={isAdmin}
            activeBusy={activeBusy}
            onSelect={handleSelect}
            onNew={handleNew}
            onDashboard={() => setViewPersist('dashboard')}
            onSettings={() => setViewPersist('settings')}
            onRbac={() => setViewPersist('rbac')}
            onMulti={() => setViewPersist('multi')}
            onTenants={isAdmin ? () => setViewPersist('tenants') : undefined}
            onAdminUsers={isAdmin ? () => setViewPersist('users') : undefined}
            onHealth={() => setViewPersist('health')}
            onHistory={() => setViewPersist('history')}
            onFragments={() => setViewPersist('fragments')}
            onActivity={() => setViewPersist('activity')}
            onWorkboard={() => setViewPersist('workboard')}
            onGoals={() => setViewPersist('goals')}
            onCollab={() => openCollab(1178)}
            onNewCollab={openCollabList}
            onEditAgent={handleEditAgent}
            onSwitchAgent={isAdmin ? handleSwitchAgent : undefined}
            onVisibleAgentsChange={setRoamScope}
            error={error}
          />
        )}
        {showDetail && (
          activeSession ? (
            <AgentDetail
              sessionName={activeSession}
              session={visibleSessions.find((s) => s.name === activeSession) || null}
              data={data}
              agentState={agentStates[activeSession]}
              onBack={isMobile ? handleBack : undefined}
              onSelectSession={handleSelect}
              onEditAgent={handleEditAgent}
              onSwitchAgent={isAdmin ? handleSwitchAgent : undefined}
              onAskGroup={(name) => openNewCollab([name])}
              onOpenCollab={openCollab}
            />
          ) : (
            <main className="main-empty">Select an agent to view details</main>
          )
        )}
        {showNewDialog && (
          <NewSessionDialog
            editSession={editSession}
            onCreated={handleCreated}
            onClose={() => { setShowNewDialog(false); setEditSession(null); }}
          />
        )}
        {switchSession && (
          <AgentSwitchDialog
            session={switchSession}
            onClose={() => setSwitchSession(null)}
            onSwitched={(name) => {
              setSwitchSession(null);
              handleSelect(name);
            }}
          />
        )}
        {switcherOverlay}

        <Suspense fallback={null}>
          <DebugConsole />
        </Suspense>
        <UpdatePrompt />
        {/* Single shared toast host (#194) — StatusToast/UpdatePrompt/paFlash all
            push into the store and render here in ONE top-right stack. */}
        <GlobalToasts />
        {/* App-level voice layer: audio sink + global control pill. The pill shows
            only when AgentDetail's own voice-bar is off screen, so the persistent
            AgentOS narrator is always visible + stoppable from any view. */}
      </>
    );
  })();

  return (
    <VoiceProvider
      agentStates={agentStates}
      roamScope={roamScope}
      realtimeEnabled={!!user.realtime_enabled}
      realtimeEndpoint={user.realtime_endpoint || null}
    >
      {body}
      {/* In den Nebenansichten gibt es keine eigene Sprachleiste, dort ist die
          schwebende Pille die einzige sichtbare Kontrolle über ein laufendes
          Gespräch — und der einzige Weg, es zu beenden. */}
      <GlobalVoiceLayer miniBar={view !== 'main' || !(showDetail && activeSession)} />
    </VoiceProvider>
  );
}
